import { useLazyQuery } from "@apollo/client/react";
import SearchIcon from '@mui/icons-material/Search';
import { FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import 'moment/locale/es';
import React, { useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { adaptDelegacionTodosArray } from '../../../adapters/delegacionAdapter';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { useLocation, useNavigate } from 'react-router-dom';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import './requisitoClientesContent.scss';
import { GET_REQ_CLIENTES_BY_DELEGACION } from '../../../data/graphQL/queries/RequisitoClientes/getClientes';
import { IndicadorEstado } from '../../ContratosCreation/components/IndicadorEstado/IndicadorEstado';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { adaptClientesToExportArray, GET_REQ_CLIENTES_EXPORT } from '../../../data/graphQL/queries/RequisitoClientes/getClientesToExport';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import toast from 'react-hot-toast';
import { normalizeFilterValue } from '../../../helpers/tools';
import { downloadExcel } from '../../../utils/downloadExcel';

export const RequisitoClientesContent = ({ setChildrenAction }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const rutasStorage = localStorage.getItem('navigationRoutes');

  const [clientes, setClientes] = useState([]);

  const [delegaciones, setDelegaciones] = useState([]);
  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const [datosOriginal, setDatosOriginal] = useState([]);

  const [allowedWrite, setAllowedWrite] = useState(false);

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    txtFiltro: ''
  }, 'requisitoClientesFilters');

  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      flex: 0.0543,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <PopoverModal>
            <div className="popover-modal-content">
              {allowedWrite && <div onClick={() => handleClickEdit(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>}
              <div onClick={() => handleClickView(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
            </div>
          </PopoverModal>);

      }
    },
    {
      flex: 0.1545,
      field: 'codSap',
      headerName: 'Cliente SAP'
    },
    {
      flex: 0.2367,
      field: 'razonSocial',
      headerName: 'Razón Social'
    },
    {
      flex: 0.1430,
      field: 'cuit',
      headerName: 'CUIT'
    },
    {
      flex: 0.16,
      field: 'delegacion',
      headerName: 'Delegación'
    },
    {
      flex: 0.0998,
      field: 'fA',
      headerAlign: 'center',
      headerName: 'FA',
      renderCell: ({ row }) => <IndicadorEstado showIcon={false} type={row.fA ? 'success' : 'empty'} />
    },
    {
      flex: 0.1385,
      field: 'configurado',
      headerAlign: 'center',
      headerName: 'Configurado',
      align: 'center',
      renderCell: ({ row }) =>
        <>
          {row.configurado ? <CheckIcon className='configurado-check-icon' /> : <ClearIcon className='configurado-clear-icon' />}
        </>


    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns), [delegaciones]);

  const storageId = sessionStorage.getItem('idUsuario');

  const [getDelegaciones, { loading: delegacionesLoading, data: delegacionesData }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!delegacionesData) return;

    setDelegaciones(adaptDelegacionTodosArray(delegacionesData));
  }, [delegacionesData]);

  const [getDelegacionOrigen, { data: delegacionOrigenData }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!delegacionOrigenData) return;

    if (delegacionOrigenData.usuarioFull.success) {
      const deleg = parseInt(delegacionOrigenData.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [delegacionOrigenData]);

  const [getClientes, { loading: clientsLoading, data: clientesData }] = useLazyQuery(GET_REQ_CLIENTES_BY_DELEGACION, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!clientesData) return;

    setDatosOriginal(clientesData.clientesByDelegacion.valorEsperado ?? []);
  }, [clientesData]);

  const [getClientesToExport] = useLazyQuery(GET_REQ_CLIENTES_EXPORT, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (rutasStorage) {
      const rutas = JSON.parse(rutasStorage);
      const rutaQueSirve = location.pathname;

      setAllowedWrite(rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
      )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura);

    }
  }, [rutasStorage]);

  useEffect(() => {
    setChildrenAction(() =>
      <CustomButton
        label='Exportar'
        loading={exportLoading}
        disabled={!filters?.idDelegacion && !delegacionOrigen}
        onClick={exportExcel}
        variant='contained'>

        Exportar
      </CustomButton>
    );
  }, [setChildrenAction, exportLoading, filters, delegacionOrigen]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones({ variables: { idUsuario: JSON.parse(storageId) } });
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId) } });
  }, [storageId]);

  useEffect(() => {
    if (!clientsLoading && delegacionOrigen > 0) {
      const idDelegacion = filters?.idDelegacion || delegacionOrigen;
      getClientes({ variables: { idDelegacion: idDelegacion } });
    }
  }, [delegacionOrigen]);

  useEffect(() => {
    handleTextFilter();
  }, [datosOriginal]);

  const handleTextFilter = (val) => {
    const inputBuscar = val === 'empty' ? '' : normalizeFilterValue(filters?.txtFiltro);

    if (inputBuscar?.length >= 2) {
      setClientes(datosOriginal?.filter((datosOriginal) => {
        const codSap = datosOriginal.codSap.toString().toUpperCase();
        const razonSocial = datosOriginal.razonSocial.toUpperCase();
        const cuit = datosOriginal.cuit;

        return (
          codSap.includes(inputBuscar) ||
          razonSocial.includes(inputBuscar) ||
          cuit.includes(inputBuscar));

      }));
    } else {
      setClientes(datosOriginal);
    }
  };

  const handleClickEdit = (row) => {
    navigate('/requisitosClientes/detalleCliente',
      {
        state: {
          cliente: row,
          allowedWrite: true
        }
      }
    );
  };

  const handleClickView = (row) => {
    navigate('/requisitosClientes/detalleCliente',
      {
        state: {
          cliente: row,
          allowedWrite: false
        }
      }
    );
  };

  const exportExcel = async () => {
    try {
      setExportLoading(true);
      const idDelegacion = filters?.idDelegacion || delegacionOrigen;
      const response = await getClientesToExport({ variables: { idDelegacion: idDelegacion } });

      if (!!response.data.clientesExportByDelegacion.error) {
        throw new Error(response.data.clientesExportByDelegacion.error);
      }

      const data = adaptClientesToExportArray(response.data.clientesExportByDelegacion.valorEsperado);

      downloadExcel({
        fileName: 'RequisitosClientes',
        sheet: "Requisitos",
        tablePayload: {
          header: excelColumns,
          body: data
        }
      });

      toast.success('Exportación realizada con éxito!');
    } catch (error) {
      toast.error(error?.message || 'Ocurrió un error al intentar exportar.');

    } finally {
      setExportLoading(false);
    }
  };

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
    handleTextFilter('empty');
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);

    if (!clientsLoading) {
      getClientes({ variables: { idDelegacion: value } });
    }
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        spacing={2}
        sx={{ marginBottom: '25px' }}>

        <Grid size={{
          xs: 12,
          sm: 6,
          md: 4,
          xl: 2.5,
        }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              onChange={handleChange}
              value={filters?.idDelegacion}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion) => {
                return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{
          xs: 12,
          sm: 6,
          md: 4,
          xl: 2.5,
        }}>
          <TextField
            label='Filtrar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            onKeyUp={handleTextFilter}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    onClick={handleTextFilter}
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        rows={clientes}
        toolbar={false}
        loading={clientsLoading} />

    </div>);

};

const excelColumns = [
  "COD_SAP",
  "CUIT",
  "NOMBRE",
  "DELEGACION",
  "PERSONA_CONTACTO",
  "CARGO",
  "TELEFONO",
  "MAIL",
  "TIEMPO MAX. FACT",
  "INICIO. FACT",
  "FIN FACT",
  "HABILITADO FA",
  "DOC. RESPALDATORIA",
  "PRESENTACION FACT",
  "MAIL FACT",
  "OBSERVACIONES",
  "AUTORIZACIONES",
  "TM-AUTORIZACIONES",
  "CONFORMIDADES",
  "TM-CONFORMIDADES",
  "ENFERMERIA",
  "TM-ENFERMERIA",
  "REMITO_NH",
  "TM-REMITO_NH",
  "IDTIPOREMITONH",
  "REMITO_FK",
  "TM-REMITO_FK",
  "IDTIPOREMITOFK",
  "PRESCRIPCION",
  "TM-PRESCRIPCION",
  "VALIDACION",
  "TM-VALIDACION",
  "DOCUMENTACION_PAC",
  "TM-DOCUMENTACION_PAC",
  "ENFERMERIAGUARDIA",
  "TM-ENFERMERIAGUARDIA",
  "NUTRICIONISTA",
  "TM-NUTRICIONISTA",
  "FONOAUDIOLOGO",
  "TM-FONOAUDIOLOGO",
  "PSICOLOGO",
  "TM-PSICOLOGO",
  "KINESIOLOGO",
  "TM-KINESIOLOGO",
  "TOCUPACIONAL",
  "TM-TOCUPACIONAL",
  "ASOCIAL",
  "TM-ASOCIAL",
  "PSIQUIATRA",
  "TM-PSIQUIATRA",
  "TELEMEDICINA",
  "SUBVENTAS",
  "COTIZACIONFINANCIADOR"];